"use client";

import { useState } from "react";
import Image from "next/image";
import {
  ChevronLeft,
  ChevronRight,
  Instagram,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import type { SectionData } from "@/lib/types/cms-types";
import * as LucideIcons from "lucide-react";
import { LucideIcon } from "lucide-react";

interface LifeAtDanaProps {
  cmsData: {
    lifeAtDana?: SectionData;
  };
  onScrollToJobList?: () => void;
}

type IconName = keyof typeof LucideIcons;

export default function LifeAtDana({ cmsData, onScrollToJobList }: LifeAtDanaProps) {
  const content = cmsData.lifeAtDana?.content;

  // const style = cmsData.life?.style;

  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const defaultPhotos = [
    {
      src: "/career-page/life-at-dana-0.jpeg",
      alt: "Team building activity",
    },
    { src: "/career-page/life-at-dana-1.jpg", 
      alt: "Office celebration" },
    {
      src: "/career-page/life-at-dana-2.jpeg",
      alt: "Workplace environment",
    },
  ];

  const defaultPerks = [
    {
      icon: "Laptop",
      title: "Remote Work Flexibility",
      description: "Work from anywhere, anytime",
    },
    {
      icon: "Heart",
      title: "Comprehensive Health Insurance",
      description: "Take care of yourself and your loved ones",
    },
    {
      icon: "GraduationCap",
      title: "Continuous Learning",
      description: "Grow your skills with our learning programs",
    },
    {
      icon: "Users",
      title: "Collaborative Culture",
      description: "Work with the best minds in the industry",
    },
  ];

  const photos = content?.photos?.length ? content.photos : defaultPhotos;
  const perks = content?.perks?.length ? content.perks : defaultPerks;

  const nextPhoto = () => {
    setCurrentPhotoIndex((prevIndex) => (prevIndex + 1) % photos.length);
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex(
      (prevIndex) => (prevIndex - 1 + photos.length) % photos.length
    );
  };

  return (
    <section
      className="w-full max-w-full overflow-x-hidden box-border py-16 px-4 sm:px-6 lg:px-8"
      style={{
        background: "#328ee9",
      }}
    >
      <div className="max-w-7xl mx-auto w-full min-w-0">
        <h2
          className="text-4xl font-bold mb-4"
          style={{
            color: "white",
            fontSize: "4xl",
          }}
        >
          {content?.title || "Life at DANA"}
        </h2>
        <p className="text-xl mb-12" style={{ color: "white" }}>
          {content?.description ||
            "Where innovation, collaboration, and growth come together."}
        </p>

        <div className="grid lg:grid-cols-2 gap-12 min-w-0">
          {/* Left Column: Visual Showcase */}
          <div className="space-y-8 min-w-0">
            {/* YouTube Video Embed (from CMS, with fallback) */}
            <div className="relative aspect-video rounded-lg overflow-hidden">
              <iframe
                src={
                  content?.videoUrl ||
                  "https://www.youtube.com/embed/8N3ZeKLktfw?si=xRuOAkiwaHH8W7uR"
                }
                title={content?.title || "Life at DANA"}
                className="absolute top-0 left-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>

            {/* Instagram Link (from CMS, with fallback to handle) */}
            <a
              href={`https://www.instagram.com/${(
                content?.instagramHandle || "daysindana"
              ).replace("@", "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-wrap items-center gap-2 text-[#328ee9] hover:text-[#328ee9]/80 transition-colors min-w-0"
            >
              <Instagram className="h-6 w-6 text-white" />
              <span className="text-white">
                Follow us on Instagram for behind-the-scenes moments
              </span>
            </a>

            {/* Workplace/Event Photos Carousel */}
            <div className="relative w-full max-w-full min-w-0">
              <div className="overflow-hidden rounded-lg aspect-[3/2] w-full max-w-[600px] mx-auto">
                <Image
                  src={photos[currentPhotoIndex].src}
                  alt={photos[currentPhotoIndex].alt}
                  width={600}
                  height={400}
                  className="w-full h-full object-cover transition-transform duration-500 ease-in-out transform hover:scale-105"
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                className="absolute top-1/2 left-2 -translate-y-1/2 bg-white/80 hover:bg-white"
                onClick={prevPhoto}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="absolute top-1/2 right-2 -translate-y-1/2 bg-white/80 hover:bg-white"
                onClick={nextPhoto}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Right Column: Perks and Opportunities */}
          <div className="min-w-0">
            <h3 className="text-3xl font-bold text-white mb-4">
              Why Join DANA?
            </h3>
            <p className="text-lg text-white mb-8">
              Discover what makes us stand out.
            </p>

            <div className="grid gap-6">
              {perks.map((perk, index) => {
                const IconComponent = perk.icon
                  ? (LucideIcons[
                      perk.icon as IconName
                    ] as LucideIcon)
                  : null;

                return (
                  <Card
                    key={index}
                    className="transition-all duration-300 hover:shadow-lg bg-white"
                  >
                    <CardContent className="flex items-start p-6">
                      {IconComponent && (
                        <IconComponent className="h-8 w-8 text-[#328ee9] mr-4 flex-shrink-0" />
                      )}
                      <div>
                        <h4 className="text-lg font-semibold mb-2">
                          {perk.title}
                        </h4>
                        <p className="text-gray-600">{perk.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>

        {/* Call-to-Action */}
        <div className="mt-16 text-center">
          <p className="text-xl mb-6 font-bold text-white">
            Ready to start your journey with DANA?
          </p>
          <Button
            size="lg"
            className="bg-white hover:bg-gray-100 text-[#328ee9] hover:text-[#2670bc] transition-colors"
            asChild
          >
            <a
              href="/#job-list"
              onClick={(e) => {
                if (onScrollToJobList) {
                  e.preventDefault();
                  onScrollToJobList();
                }
              }}
            >
              Explore Opportunities
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}
